import { faUserCheck, faUserXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableRow, TableCell, Grid, Typography, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { TnAAbsenceDTO } from "../../../../../../../models/tna";
import CommentBubble from "../../../../../../../reusable-components/CommentBubble/CommentBubble";
import { intlFormatDateShort } from "../../../../../../../utils/time-utils";
import AttachedDocuments from "../../../../../../AttachedDocuments/AttachedDocuments";
import AbsenceButton from "../../../../../../MyCalendar/AddEditAbsence/AbsenceButton";
import { AbsenceStatusIndicator } from "../../../../../../MyCalendar/AddEditAbsence/styles";

interface Props {
  absence: TnAAbsenceDTO;
}

const OverlappingAbsencesRow = ({ absence }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_absence";
  const theme = useTheme();

  return (
    <TableRow>
      <TableCell align={"center"}>
        <AbsenceStatusIndicator
          isRejected={absence.isRejected}
          isCancelled={absence.isCancelled}
          isApproved={absence.isApproved}
          key={absence.id}
        >
          <AbsenceButton
            absence={absence}
            wrapContent
          />
        </AbsenceStatusIndicator>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>{absence.employee?.name ?? ""}</Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          {absence?.absenceType?.name && <Typography>{absence.absenceType.name}</Typography>}
          {absence?.comment && (
            <CommentBubble
              title={`${t(`${tRP}.fields.comment`)}:`}
              comment={absence.comment}
            />
          )}
        </Grid>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>
          {absence?.fromDate && (
            <Typography>
              {intlFormatDateShort(new Date(absence.fromDate.year, absence.fromDate.month - 1, absence.fromDate.day))}
            </Typography>
          )}
        </Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>
          {absence?.toDate && (
            <Typography>
              {intlFormatDateShort(new Date(absence.toDate.year, absence.toDate.month - 1, absence.toDate.day))}
            </Typography>
          )}
        </Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Grid
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            height: "50px",
          }}
        >
          {!!absence?.documentation && (
            <AttachedDocuments
              count={absence.documentation?.filesCount}
              documentIDs={[absence.documentation?.id]}
              buttonSize={"2x"}
              documentsReadOnlyFields={[
                {
                  documentID: absence.documentation?.id,
                  readOnlyFields: {
                    isEmployeeRO: true,
                    isCategoryRO: true,
                    isLegalEntityRO: true,
                    isTypeRO: true,
                    isDateRO: true,
                    isStatusRO: true,
                  },
                },
              ]}
              hideReadOnlyFields
              useSimpleView
              documentIsReadOnly
            />
          )}
        </Grid>
      </TableCell>

      <TableCell align={"center"}>
        <Typography>
          {absence?.isApproved && !absence?.isCancelled ? (
            <Typography title={`Approved by ${absence?.modifiedByUser?.name}`}>
              {absence?.modifiedByUser?.name}
            </Typography>
          ) : absence?.isRejected && !absence?.isCancelled ? (
            <Typography title={`Rejected by ${absence?.modifiedByUser?.name}`}>
              {absence?.modifiedByUser?.name}
            </Typography>
          ) : (
            <></>
          )}
        </Typography>
      </TableCell>

      <TableCell align={"center"}>
        <Grid sx={{ display: "flex", flexDirection: "row", gap: "5px", flexWrap: "wrap" }}>
          {absence?.acceptedRejectedByUser?.name && <Typography>{absence.acceptedRejectedByUser.name}</Typography>}
          {absence?.acceptedByClient ? (
            <Typography>
              <FontAwesomeIcon
                icon={faUserCheck}
                color={theme.palette.success.main}
              />
            </Typography>
          ) : absence?.rejectedByClient ? (
            <Typography>
              <FontAwesomeIcon
                icon={faUserXmark}
                color={theme.palette.error.main}
              />
            </Typography>
          ) : (
            <></>
          )}
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default OverlappingAbsencesRow;
